import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Inline badge that surfaces, per platform, which post-management actions
 * are supported by the underlying API. Backed by GET /api/v1/posts/capabilities.
 *
 * Props:
 *   platform        — "facebook" | "instagram" | "tiktok"
 *   capabilities    — output of /capabilities, shape:
 *                     { platforms: { <platform>: {publish, edit, delete} },
 *                       connected: { <platform>: bool },
 *                       notes:     { <platform>: string[] } }
 *   action          — "edit" | "delete" (which capability to render)
 *   compact         — when true, render only the icon without the label
 */
const ICON_OK = '✓';
const ICON_NO = '✗';

// Social-platform icons rendered inside the badge so the user can see
// at a glance which channel each capability badge refers to.
const PLATFORM_ICON = {
  instagram: '📸',
  facebook: '📘',
  tiktok: '🎵',
};

const PLATFORM_LABEL = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  tiktok: 'TikTok',
};

function PlatformCapabilityBadge({ platform, capabilities, action, compact = false }) {
  const { t } = useTranslation();

  if (!capabilities || !capabilities.platforms || !platform || !action) {
    return null;
  }

  const platformCaps = capabilities.platforms[platform];
  if (!platformCaps) return null;

  const supported = !!platformCaps[action];
  const i18nKeyForUnsupported = (() => {
    if (action === 'edit' && platform === 'instagram') {
      return 'singlePost.instagramEditUnsupported';
    }
    if (action === 'edit' && platform === 'tiktok') {
      return 'singlePost.tiktokEditUnsupported';
    }
    if (action === 'delete' && platform === 'instagram') {
      return 'singlePost.instagramDeleteUnsupported';
    }
    if (action === 'delete' && platform === 'tiktok') {
      return 'singlePost.tiktokDeleteUnsupported';
    }
    return null;
  })();

  const tooltip = supported
    ? t(`singlePost.actionSupported.${action}`, { defaultValue: '' })
    : i18nKeyForUnsupported
      ? t(i18nKeyForUnsupported)
      : t('singlePost.actionUnsupportedGeneric', { defaultValue: 'Not supported by API' });

  const label = action === 'edit'
    ? t('singlePost.edit')
    : t('singlePost.delete');

  const className = `platform-capability-badge ${supported ? 'cap-supported' : 'cap-unsupported'}`;

  const platformIcon = PLATFORM_ICON[platform] || null;
  const platformLabel = PLATFORM_LABEL[platform] || platform;
  const fullTooltip = `${platformLabel} — ${tooltip || (supported ? 'OK' : 'Non supportato')}`;

  return (
    <span
      className={className}
      title={fullTooltip}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.25rem',
        padding: '0.15rem 0.45rem',
        borderRadius: '6px',
        fontSize: '0.75rem',
        lineHeight: 1.2,
        background: supported ? 'rgba(40,167,69,0.1)' : 'rgba(220,53,69,0.1)',
        color: supported ? '#28a745' : '#dc3545',
        cursor: 'help',
      }}
    >
      {platformIcon && (
        <span aria-hidden="true" style={{ fontSize: '0.85rem' }}>
          {platformIcon}
        </span>
      )}
      <span aria-hidden="true">{supported ? ICON_OK : ICON_NO}</span>
      {!compact && <span>{label}</span>}
    </span>
  );
}

export default PlatformCapabilityBadge;
